<script lang="ts">
  // CinematicCollect — overlay que coleta as cartas da mesa em anti-horario
  // e as guarda no monte:
  //
  //   step 1: TOP → LEFT
  //   step 2: TOP+LEFT → BOTTOM
  //   step 3: 3 → RIGHT
  //   step 4: TUDO + VIRA → CENTRO (front ainda visivel)
  //   step 5: FLIP — rotateY(180) + cross-fade pra back roxo
  //   step 6: ENCOLHE + opacity 0 (vai pro deck)
  //
  // Front e back sao dois layers sobrepostos (position:absolute inset:0)
  // com opacity controlada por `flipped`. rotateY no pai da a sensacao de
  // virar; cross-fade garante que o back ROXO REALMENTE aparece —
  // independente de backface-visibility (que mostrou inconsistencia).

  import { onMount } from 'svelte';
  import Card from './Card.svelte';
  import type { Card as CardType } from '../../stores/match';

  type Pos = { x: number; y: number };
  type Props = {
    topCard?: CardType | null;
    leftCard?: CardType | null;
    bottomCard?: CardType | null;
    rightCard?: CardType | null;
    viraCard?: CardType | null;
    durationMs?: number;
    topPos: Pos;
    leftPos: Pos;
    bottomPos: Pos;
    rightPos: Pos;
    centerPos: Pos;
    onDone?: () => void;
  };
  let {
    topCard,
    leftCard,
    bottomCard,
    rightCard,
    viraCard,
    durationMs = 1500,
    topPos,
    leftPos,
    bottomPos,
    rightPos,
    centerPos,
    onDone,
  }: Props = $props();

  let step = $state(0);

  onMount(() => {
    const d = durationMs;
    const timers: number[] = [];
    timers.push(window.setTimeout(() => (step = 1), Math.round(d * 0.03)));
    timers.push(window.setTimeout(() => (step = 2), Math.round(d * 0.18)));
    timers.push(window.setTimeout(() => (step = 3), Math.round(d * 0.33)));
    timers.push(window.setTimeout(() => (step = 4), Math.round(d * 0.48)));
    // Step 5 ATIVA tudo de uma vez: flip + descida pro deck + encolhe sutil.
    // Felipe 2026-05-20: enquanto vira, ja desce — movimento continuo
    // mais natural que sequencial.
    timers.push(window.setTimeout(() => (step = 5), Math.round(d * 0.60)));
    timers.push(window.setTimeout(() => {
      if (onDone) onDone();
    }, d));
    return () => timers.forEach(clearTimeout);
  });

  let topTarget = $derived.by((): Pos => {
    switch (step) {
      case 1: return leftPos;
      case 2: return bottomPos;
      case 3: return rightPos;
      case 4: case 5: case 6: return centerPos;
      default: return topPos;
    }
  });
  let leftTarget = $derived.by((): Pos => {
    switch (step) {
      case 2: return bottomPos;
      case 3: return rightPos;
      case 4: case 5: case 6: return centerPos;
      default: return leftPos;
    }
  });
  let bottomTarget = $derived.by((): Pos => {
    switch (step) {
      case 3: return rightPos;
      case 4: case 5: case 6: return centerPos;
      default: return bottomPos;
    }
  });
  let rightTarget = $derived.by((): Pos => {
    switch (step) {
      case 4: case 5: case 6: return centerPos;
      default: return rightPos;
    }
  });

  let flipped = $derived(step >= 5);

  function cardStyle(target: Pos): string {
    // Step 5 ativa tudo simultaneamente:
    //  - rotateY 180 (flip pra back)
    //  - scale 0.92 (encolhe sutil)
    //  - translateY +10px (desce pro deck, que fica abaixo da vira)
    // Movimento continuo de "girar enquanto desce e encaixa no deck".
    const rotY = flipped ? 180 : 0;
    const scale = flipped ? 0.92 : 1;
    const deckShiftY = flipped ? 10 : 0;
    return `left: ${target.x}px; top: ${target.y}px; transform: translate(-50%, calc(-50% + ${deckShiftY}px)) rotateY(${rotY}deg) scale(${scale});`;
  }
  let topStyle = $derived(cardStyle(topTarget));
  let leftStyle = $derived(cardStyle(leftTarget));
  let bottomStyle = $derived(cardStyle(bottomTarget));
  let rightStyle = $derived(cardStyle(rightTarget));
  let viraStyle = $derived(cardStyle(centerPos));
</script>

<div class="cinematic-overlay">
  {#if topCard}
    <div class="anim-card" style={topStyle}>
      <div class="layer front" class:hidden={flipped}><Card card={topCard} size="md" /></div>
      <div class="layer back" class:visible={flipped}><Card card={null} size="md" /></div>
    </div>
  {/if}
  {#if leftCard}
    <div class="anim-card" style={leftStyle}>
      <div class="layer front" class:hidden={flipped}><Card card={leftCard} size="md" /></div>
      <div class="layer back" class:visible={flipped}><Card card={null} size="md" /></div>
    </div>
  {/if}
  {#if bottomCard}
    <div class="anim-card" style={bottomStyle}>
      <div class="layer front" class:hidden={flipped}><Card card={bottomCard} size="md" /></div>
      <div class="layer back" class:visible={flipped}><Card card={null} size="md" /></div>
    </div>
  {/if}
  {#if rightCard}
    <div class="anim-card" style={rightStyle}>
      <div class="layer front" class:hidden={flipped}><Card card={rightCard} size="md" /></div>
      <div class="layer back" class:visible={flipped}><Card card={null} size="md" /></div>
    </div>
  {/if}
  {#if viraCard}
    <div class="anim-card" style={viraStyle}>
      <div class="layer front" class:hidden={flipped}><Card card={viraCard} size="md" /></div>
      <div class="layer back" class:visible={flipped}><Card card={null} size="md" /></div>
    </div>
  {/if}
</div>

<style>
  .cinematic-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 10;
    perspective: 1000px;
  }
  .anim-card {
    position: absolute;
    width: var(--card-w-md);
    height: calc(var(--card-w-md) * 1.5);
    transition:
      left 350ms cubic-bezier(0.4, 0.1, 0.5, 1),
      top 350ms cubic-bezier(0.4, 0.1, 0.5, 1),
      transform 400ms cubic-bezier(0.3, 0.0, 0.5, 1),
      opacity 350ms ease-out;
    will-change: transform, left, top, opacity;
  }
  .layer {
    position: absolute;
    inset: 0;
    transition: opacity 250ms ease-in-out;
  }
  .layer.front { opacity: 1; }
  .layer.front.hidden { opacity: 0; }
  .layer.back { opacity: 0; }
  .layer.back.visible { opacity: 1; }
</style>
